import { _ } from "../../polyglot/polyglot.js";
import {
	downloadIcon,
	plusIcon,
	timesIcon,
	undoIcon,
	uploadIcon,
} from "../../elements/icons/index.js";
import AsyncStateWidget from "../../chrome/AsyncStateWidget.js";
import FileSelectionButtonWidget from "../../widgets/FileSelectionButtonWidget.js";
import ListWidget from "../../widgets/list/ListWidget/ListWidget.js";

/**
 * `ModelsSettingsDocumentSection` allows the user to manage
 * a given set of models. The receiver is tightely conected to a
 * service which does the heavy job of CRUD operations. As a result,
 * the receiver is only responsible for listing models and providing
 * simple actions for each (e.g., download, upload, restore).
 *
 * @param {service} service - Used to do CRUD operations on models.
 * @param {Renderable} [emptyLabel] - Text used when the set of models is empty.
 * @param {Renderable} [uploadButtonLabel] - Text used as the label of the upload button.
 */
export default class ModelsSettingsDocumentSection extends AsyncStateWidget {
	constructor({
		service,
		emptyLabel = _("No data"),
		uploadButtonLabel,
	} = {}) {
		super(...arguments);

		this._service = service;
		this._emptyLabel = emptyLabel;
		this._uploadButtonLabel = uploadButtonLabel;

		this._listWidget = new ListWidget({
			cssClass: "list-stripped clear-fix models-list",
			itemRenderer: this._modelRenderer.bind(this),
		});
	}

	show() {
		this._updateModels();
	}

	_renderEmptyContentOn(html) {
		if (this._uploadButtonLabel) {
			html.div(
				{ klass: "models-settings-section", "data-test": "no-data" },
				this._renderUploadButtonOn.bind(this),
				this._emptyLabel,
			);
		} else {
			html.div({ "data-test": "no-data" }, this._emptyLabel);
		}
	}

	_renderLoadedContentOn(html) {
		if (this._uploadButtonLabel) {
			html.div(
				{ klass: "models-settings-section", "data-test": "models" },
				this._renderUploadButtonOn.bind(this),
				this._listWidget,
			);
		} else {
			html.div({ "data-test": "models" }, this._listWidget);
		}
	}

	_renderUploadButtonOn(html) {
		html.render(
			FileSelectionButtonWidget.forJSONFile({
				label: html.div(plusIcon, this._uploadButtonLabel),
				cssClass: "btn icon-and-text btn-primary",
				fileSelectedCallback: (json) => {
					this._service.create({
						model: json,
						callback: () => this.show(),
						successMessage: _(
							"Shared report template successfully saved",
						),
					});
				},
			}),
		);
	}

	_modelRenderer(model) {
		return (html) => {
			html.div(
				{ class: "clearfix model-item", "data-test": "model" },
				this._modelNameRenderer(model),
				this._modelActionsRenderer(model),
			);
		};
	}

	_modelNameRenderer(model) {
		return (html) => {
			html.div(
				{ class: "item-info" },
				html.span(
					{ class: "item-name", "data-test": "name" },
					this._modelTitle(model),
				),
				this._modelStatusRenderer(model),
			);
		};
	}

	_modelStatusRenderer(model) {
		return (html) => {
			if (this._service.canRestore(model)) {
				html.div(
					{ class: "item-status custom-template" },
					html.span({ class: "status-label" }, _("Custom")),
				);
			} else {
				html.div(
					{ class: "item-status default-template" },
					html.span({ class: "status-label" }, _("Default")),
				);
			}
		};
	}

	_modelTitle(model) {
		const newName = model.getName();
		const knownName = model.getKnownName();

		if (!newName) {
			return knownName || _("<no name>");
		}

		if (!knownName || knownName.toString() === newName.toString()) {
			return newName;
		}

		return [
			knownName,
			" ",
			(html) => html.small(_("(renamed to '{newName}')", { newName })),
		];
	}

	_modelActionsRenderer(model) {
		return (html) =>
			html.div(
				{ class: "actions pull-right" },
				this._deleteLinkRenderer(model),
				this._restoreLinkRenderer(model),
				this._uploadLinkRenderer(model),
				this._downloadLinkRenderer(model),
			);
	}

	_restoreLinkRenderer(model) {
		return (html) => {
			if (!this._service.canRestore(model)) {
				return;
			}

			html.a(
				{
					class: "btn",
					"data-test": "restore-model",
					click: () => this._restore(model),
				},
				undoIcon({
					title: _("Restore"),
				}),
			);
		};
	}

	_deleteLinkRenderer(model) {
		return (html) => {
			if (!this._service.canDelete(model)) {
				return;
			}

			html.a(
				{
					class: "btn",
					"data-test": "delete-model",
					click: () => this._delete(model),
				},
				timesIcon({
					title: _("Delete"),
				}),
			);
		};
	}

	_uploadLinkRenderer(model) {
		return FileSelectionButtonWidget.forJSONFile({
			label: uploadIcon({
				title: _("Upload"),
				dataTest: "upload",
			}),
			fileSelectedCallback: (json) => {
				json.id = model.getId();

				this._service.save({
					modelData: json,
					callback: () => this._updateModels(),
				});
			},
		});
	}

	_downloadLinkRenderer(model) {
		return (html) => {
			html.a(
				{
					class: "btn",
					href: this._service.getDownloadUrlFor(model),
					download: `${model.getId()}.json`,
					"data-test": "download",
				},
				downloadIcon({ title: _("Download") }),
			);
		};
	}

	_updateModels() {
		this._service.findAll((models = []) => {
			this._listWidget.setItems(models);

			if (this._listWidget.isEmpty()) {
				this._showEmpty();
			} else {
				this._showContent();
			}
		});
	}

	_restore(model) {
		const name = this._modelTitle(model);

		this._confirmDeletion({
			title: _("Restore {name}?", { name }),
			body: _(
				"Are you sure you want to permanently restore the uploaded '{name}' and go back to its original version?",
				{ name },
			),
			callback: () =>
				this._service.delete({
					modelId: model.getId(),
					callback: () => this._updateModels(),
				}),
		});
	}

	_delete(model) {
		this._confirmDeletion({
			name: this._modelTitle(model),
			callback: () => {
				this._service.delete({
					modelId: model.getId(),
					callback: () => this._updateModels(),
				});
			},
		});
	}
}
